import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DemoPill, FilterMultiSelect, FilterSelect, Segmented, SearchInput, StateMessage } from '@/components/ui/controls';
import { PageHeader } from '@/components/ui/page';
import { btn } from '@/components/ui/styles';
import StatTile from '@/components/ui/stat-tile';
import type { CasinoCard, CasinoList } from '@/types/api';
import { countryName, formatCount, formatMoney } from '@/utils/format';
import { useApi, useDebounced } from '@/utils/use-api';
import { useSegments } from '@/utils/use-segments';
import { sortRows, type Sort } from '@/utils/use-sort';

import CasinoCardView from './casino-card';
import CasinoTable, { CASINO_DEFAULT_SORT, CASINO_SORT, CASINO_SORT_OPTIONS } from './casino-table';

type View = 'cards' | 'table';

const DEPLOYING = 'content_deployment_in_progress';
const NO_CASINOS: CasinoCard[] = [];
/** An order as the URL and the cards' select write it: the column, with a leading "-" the other way (as on Leads). */
const sortParamOf = (s: Sort) => (s.desc ? '-' : '') + s.key;

/**
 * Casinos (CAS-1…3, SEG-1): every casino mirrored from Owner Hub, with its owner
 * and 30-day performance. A table for comparing, the busiest first, by default
 * (2026-11-03); cards for scanning; both show the same numbers. A phone always
 * shows the cards (UI-1).
 */
const CasinosPage = () => {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  // Segments and countries each take several ids, comma-separated (SEG-2).
  const segment = params.get('segment') ?? '';
  const country = params.get('country') ?? '';
  const ids = (v: string) => v.split(',').filter(Boolean);
  const view: View = params.get('view') === 'cards' ? 'cards' : 'table';
  const [query, setQuery] = useState(params.get('q') ?? '');
  const debounced = useDebounced(query);

  const update = (patch: Record<string, string | null>) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );
  useEffect(() => {
    if ((params.get('q') ?? '') !== debounced) update({ q: debounced || null });
    // Only the debounced text drives this; `params` and `update` change on every navigation.
  }, [debounced]);

  // Fetched with the casinos, so the activity tags show their segments' colours at once,
  // and the segment filter has its options.
  const segments = useSegments();
  const { data, error, loading } = useApi<CasinoList>('/casinos', {
    q: params.get('q') ?? undefined,
    status: status || undefined,
    segment: segment || undefined,
    country: country || undefined,
  });
  const filtered = Boolean(params.get('q') || status || segment || country);
  // One order for the table and the cards, kept in the URL: the table's headers and the cards' select set it.
  const sortParam = params.get('sort') ?? '';
  const sortKey = sortParam.replace(/^-/, '');
  const sort: Sort = CASINO_SORT[sortKey] ? { key: sortKey, desc: sortParam.startsWith('-') } : CASINO_DEFAULT_SORT;
  const setSort = (s: Sort) => update({ sort: sortParamOf(s) === sortParamOf(CASINO_DEFAULT_SORT) ? null : sortParamOf(s) });
  const sortBy = (key: string) => setSort(sort.key === key ? { key, desc: !sort.desc } : { key, desc: Boolean(CASINO_SORT[key]?.desc) });
  const rows = useMemo(() => sortRows(data?.items ?? NO_CASINOS, CASINO_SORT, sort), [data, sort.key, sort.desc]);
  const sortOption = CASINO_SORT_OPTIONS.find((o) => o.key === sort.key) ?? CASINO_SORT_OPTIONS[0];
  const sortOrder = `${sortOption.label}, ${sort.desc ? sortOption.desc : sortOption.asc}`;
  const mock = data?.metrics_source === 'mock';
  const hasMetrics = data && data.metrics_source !== 'none';
  const totals = data?.totals;

  return (
    <div>
      <PageHeader
        title="Casinos"
        description="Every casino our owners started, with its owner and the last 30 days of play."
        actions={
          mock && (
            <DemoPill title="GGR, players and velocity are made-up numbers: this is the local mock metrics source.">
              Demo metrics
            </DemoPill>
          )
        }
      >
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput value={query} onChange={setQuery} label="Search casinos" placeholder="Search casino, domain or owner" />
          {/* Deploying and a segment or a country never go together: choosing one clears the other. */}
          <FilterSelect
            label="Status"
            value={status}
            onChange={(v) => update({ status: v || null, segment: v === DEPLOYING ? null : segment || null, country: v === DEPLOYING ? null : country || null })}
          >
            <option value="">All statuses</option>
            <option value="ok">Live</option>
            <option value={DEPLOYING}>Deploying</option>
          </FilterSelect>
          {/* Each filter takes one of its values; with both, the casinos in both. */}
          <FilterMultiSelect
            label="Segments"
            allLabel="All segments"
            value={ids(segment)}
            onChange={(v) => update({ segment: v.join(',') || null, status: v.length && status === DEPLOYING ? null : status || null })}
            options={segments.items.filter((s) => !s.country).map((s) => ({ value: s.id, label: s.name }))}
          />
          {/* Each country's own segment (SEG-2), apart from the team's. */}
          <FilterMultiSelect
            label="Countries"
            allLabel="All countries"
            value={ids(country)}
            onChange={(v) => update({ country: v.join(',') || null, status: v.length && status === DEPLOYING ? null : status || null })}
            options={segments.items.filter((s) => s.country).map((s) => ({ value: s.id, label: countryName(s.country) }))}
          />
          {/* The order, beside the view, for the table and the cards alike: what to sort by, then
              which way. A column picked starts the way its header's first click does. */}
          <div className="flex items-center gap-2 max-md:min-w-[11rem] max-md:flex-1 md:ml-auto">
            <span aria-hidden className="hidden text-sm text-fg-muted md:inline">
              Sort by
            </span>
            <FilterSelect
              label="Sort by"
              value={sort.key}
              // Tinted once the order is not the default one.
              idle={sortParamOf(sort) === sortParamOf(CASINO_DEFAULT_SORT) ? sort.key : ''}
              onChange={(key) => setSort({ key, desc: Boolean(CASINO_SORT[key]?.desc) })}
            >
              {CASINO_SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </FilterSelect>
            <button
              type="button"
              aria-label={`${sortOrder}. Reverse the order`}
              title={sortOrder}
              onClick={() => setSort({ key: sort.key, desc: !sort.desc })}
              // !px-0: btn.secondary's px-4 comes later in Tailwind's CSS, so a plain px-0 would lose.
              className={classNames(btn.secondary, 'w-11 !px-0 md:w-9')}
            >
              <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d={sort.desc ? 'M8 3v10M4 9l4 4 4-4' : 'M8 13V3M4 7l4-4 4 4'} />
              </svg>
            </button>
            <div className="hidden md:block">
              <Segmented<View>
                label="View"
                value={view}
                onChange={(v) => update({ view: v === 'cards' ? 'cards' : null })}
                options={[
                  { value: 'cards', label: 'Cards' },
                  { value: 'table', label: 'Table' },
                ]}
              />
            </div>
          </div>
        </div>
      </PageHeader>

      {error && !data ? (
        <StateMessage title="Casinos could not be loaded" tone="error">
          {error}
        </StateMessage>
      ) : (
        <div className={classNames('transition-opacity', loading && data && 'opacity-60')}>
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile label="Casinos" value={totals ? String(totals.casinos) : '—'} note={totals ? `${totals.live} live` : undefined} />
            <StatTile label="GGR, last 30 days" value={hasMetrics && totals ? formatMoney(totals.ggr_30d_usd) : '—'} note="Live casinos, USD" />
            <StatTile label="Players, last 30 days" value={hasMetrics && totals ? formatCount(totals.players_30d) : '—'} note="Live casinos, busiest day" />
            <StatTile label="Average velocity" value={hasMetrics && totals ? totals.avg_velocity.toFixed(1) : '—'} note="Activity score, 0–100" />
          </div>

          {!data ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-44 animate-pulse rounded-card bg-surface shadow-e1" />
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <StateMessage title={filtered ? 'No casino matches these filters' : 'No casinos yet'}>
              {filtered ? 'Try another search, status or segment.' : 'Casinos appear here once an owner launches one.'}
            </StateMessage>
          ) : (
            <>
              <div className={classNames('grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3', view === 'table' && 'md:hidden')}>
                {rows.map((c) => (
                  <CasinoCardView key={c.id} casino={c} />
                ))}
              </div>
              {view === 'table' && (
                <div className="hidden md:block">
                  <CasinoTable rows={rows} sort={sort} onSort={sortBy} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CasinosPage;
